import type { ReactNode } from 'react';

import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@/ui/DropdownMenu';
import { currencyFormatter } from '@/utils/currencyFormatter';
import { dateFormatter } from '@/utils/dateFormatter';

import type { GroupSubscriptionData } from '../groupSubscriptionTypes';
import { InviteMenu } from './InviteMenu';

type GroupCardHeaderProps = {
  group: GroupSubscriptionData;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
  hideToggle?: boolean;
  hideInviteMenu?: boolean;
  rightSlot?: ReactNode;
  endedAt?: string | null;
  totalCents?: number | null;
  showSeatCount?: boolean;
  onManageSeats?: (groupId: string) => void;
  onViewBilling?: (groupId: string) => void;
  onCancelSubscription?: (groupId: string) => void;
  onRestoreSubscription?: (groupId: string) => void;
  onInviteByEmail?: (groupId: string) => void;
  onBulkInviteCsv?: (groupId: string) => void;
  translations?: Record<string, string>;
};

export function GroupCardHeader({
  group,
  isExpanded = false,
  onToggleExpanded,
  hideToggle = false,
  hideInviteMenu = false,
  rightSlot,
  endedAt,
  totalCents,
  showSeatCount = false,
  onManageSeats,
  onViewBilling,
  onCancelSubscription,
  onRestoreSubscription,
  onInviteByEmail,
  onBulkInviteCsv,
  translations,
}: GroupCardHeaderProps) {
  const planSuffix = translations?.['group_subscriptions.card.plan_suffix'] ?? 'plan';
  const managePlanLabel = translations?.['group_subscriptions.card.manage_plan'] ?? 'Manage plan';
  const manageSeatsLabel = translations?.['group_subscriptions.card.manage_seats'] ?? 'Manage seats';
  const billingDetailsLabel = translations?.['group_subscriptions.card.billing_details'] ?? 'Billing details';
  const cancelSubscriptionLabel =
    translations?.['group_subscriptions.card.cancel_subscription'] ?? 'Cancel subscription';
  const keepSubscriptionLabel = translations?.['group_subscriptions.card.keep_subscription'] ?? 'Keep subscription';
  const inviteLabel = translations?.['group_subscriptions.card.invite'] ?? 'Invite';

  const perSeatPrice = currencyFormatter({ value: group.per_seat_amount_cents / 100, currency: group.currency });
  const totalCentsForDisplay = totalCents ?? group.total_amount_cents;
  const totalPrice =
    totalCentsForDisplay != null
      ? currencyFormatter({ value: totalCentsForDisplay / 100, currency: group.currency })
      : null;
  // Trailing-date line, in priority order:
  //   ended  — `endedAt` prop set (CanceledGroupCard) → "Ended on X"
  //   ends   — active but scheduled to cancel at renewal (`ends_at`) → "Ends on X"
  //   renews — normal active → "Renews on X"
  // `endedAt !== undefined` (not truthiness): a canceled card may pass an explicit
  // null endedAt, and that must still win over `renews_at` rather than fall
  // through to "Renews on …" — see CanceledGroupCard.
  const endsAt = group.ends_at;
  let trailingKind: 'ended' | 'ends' | 'renews' = 'renews';
  if (endedAt !== undefined) trailingKind = 'ended';
  else if (endsAt) trailingKind = 'ends';
  const trailingDate = { ended: endedAt, ends: endsAt, renews: group.renews_at }[trailingKind];
  // Force UTC — these are calendar dates stored as UTC midnight.
  const trailingDateFormatted = trailingDate ? dateFormatter({ timeZone: 'UTC' }).format(new Date(trailingDate)) : null;

  const isOneTime = group.interval === 'one_time';

  const humanizedInterval = group.interval.replace(/_/g, ' ');
  const perSeatLine = (translations?.['group_subscriptions.card.per_seat_per_unit'] ?? '{{price}} per seat/{{unit}}')
    .replace('{{price}}', perSeatPrice)
    .replace('{{unit}}', humanizedInterval);

  let totalLine: string | null = null;
  if (totalPrice) {
    totalLine = isOneTime
      ? (translations?.['group_subscriptions.card.total'] ?? 'Total {{total}}').replace('{{total}}', totalPrice)
      : (translations?.['group_subscriptions.card.total_per_year'] ?? 'Total {{total}}/year').replace(
          '{{total}}',
          totalPrice,
        );
  }

  const seatsLine = showSeatCount
    ? (translations?.['group_subscriptions.card.seats_count'] ?? '{{count}} seats').replace(
        '{{count}}',
        String(group.max_seats),
      )
    : null;

  const trailingTemplates = {
    ended: { key: 'ended_on', fallback: 'Ended on {{date}}' },
    ends: { key: 'ends_on', fallback: 'Ends on {{date}}' },
    renews: { key: 'renews_on', fallback: 'Renews on {{date}}' },
  } as const;
  const { key: trailingKey, fallback: trailingFallback } = trailingTemplates[trailingKind];
  const trailingLine = trailingDateFormatted
    ? (translations?.[`group_subscriptions.card.${trailingKey}`] ?? trailingFallback).replace(
        '{{date}}',
        trailingDateFormatted,
      )
    : null;

  const toggleAriaLabel = isExpanded
    ? (translations?.['group_subscriptions.card.collapse_aria'] ?? 'Collapse {{tier}} plan').replace(
        '{{tier}}',
        group.tier_name,
      )
    : (translations?.['group_subscriptions.card.expand_aria'] ?? 'Expand {{tier}} plan').replace(
        '{{tier}}',
        group.tier_name,
      );

  // When `rightSlot` is explicitly passed (including null), it replaces the
  // action dropdowns entirely. Otherwise we render the default action menus.
  const resolvedRightSlot =
    rightSlot !== undefined ? (
      rightSlot
    ) : (
      <div className="flex shrink-0 items-center gap-2 sm:gap-4">
        <DropdownMenu>
          <DropdownMenuTrigger className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
            {managePlanLabel}
            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
          </DropdownMenuTrigger>
          <DropdownMenuPortal>
            <DropdownMenuContent align="end" sideOffset={4} className="min-w-[200px] py-1.5">
              {onManageSeats && (
                <DropdownMenuItem onSelect={() => onManageSeats(group.id)}>{manageSeatsLabel}</DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => onViewBilling?.(group.id)} className="border-b border-gray-200">
                {billingDetailsLabel}
              </DropdownMenuItem>
              {/* Active-but-canceling (ends_at set): the destructive "Cancel" is
                  already in effect, so swap it for the non-destructive "Keep
                  subscription" reversal. Gated on the handler too so a host that
                  hasn't wired restore yet falls back to "Cancel" rather than
                  rendering a dead item. Never both at once. */}
              {endsAt && onRestoreSubscription ? (
                <DropdownMenuItem onSelect={() => onRestoreSubscription(group.id)}>
                  {keepSubscriptionLabel}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={() => onCancelSubscription?.(group.id)} isDestructive>
                  {cancelSubscriptionLabel}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenuPortal>
        </DropdownMenu>
        {!hideInviteMenu && (
          <InviteMenu
            triggerClassName="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-wt-primary px-3 py-1.5 text-sm font-medium text-wt-text-on-primary shadow-sm hover:opacity-90"
            onInviteByEmail={onInviteByEmail ? () => onInviteByEmail(group.id) : undefined}
            onBulkInviteCsv={onBulkInviteCsv ? () => onBulkInviteCsv(group.id) : undefined}
            translations={translations}
          >
            <span aria-hidden="true">+</span>
            {inviteLabel}
          </InviteMenu>
        )}
      </div>
    );

  return (
    <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="flex items-start gap-2">
        {!hideToggle && onToggleExpanded && (
          <button
            type="button"
            onClick={onToggleExpanded}
            aria-expanded={isExpanded}
            aria-label={toggleAriaLabel}
            className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-900"
          >
            {isExpanded ? <ChevronDownIcon className="h-5 w-5" /> : <ChevronRightIcon className="h-5 w-5" />}
          </button>
        )}
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="text-lg font-medium text-gray-900">{group.group_name}</h3>
            <span className="inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded border border-gray-200 bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
              {group.tier_name} {planSuffix}
            </span>
            {group.status === 'past_due' && (
              <span className="inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700">
                Past due
              </span>
            )}
          </div>
          <div className="flex flex-col gap-0.5 text-sm text-gray-500 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2 sm:gap-y-1">
            <span>{perSeatLine}</span>
            {seatsLine && (
              <>
                <span aria-hidden="true" className="hidden sm:inline">
                  •
                </span>
                <span>{seatsLine}</span>
              </>
            )}
            {totalLine && (
              <>
                <span aria-hidden="true" className="hidden sm:inline">
                  •
                </span>
                <span>{totalLine}</span>
              </>
            )}
            {trailingLine && (
              <>
                <span aria-hidden="true" className="hidden sm:inline">
                  •
                </span>
                <span>{trailingLine}</span>
              </>
            )}
          </div>
        </div>
      </div>
      {resolvedRightSlot}
    </div>
  );
}
